/**
 * The unread count on Pendo's Resource Center — a bubble on the launcher and one on the
 * Produktnyheter row.
 *
 * This is the surface the announcement modal replaces, kept for customers who do not have
 * `PRODUCT_NEWS_MODAL` yet: it has been live for every customer since the panel stopped
 * being flag-gated, so it cannot come out until the modal is what everyone gets. Delete
 * this file, its lang key and its callers in `productNews.ts` once that happens.
 */
import T from 'Components/Helpers/translate';

/** The Resource Center's root, as Pendo ids it. */
export const PENDO_CONTAINER_ID = 'pendo-resource-center-container';

/**
 * The launcher, as Pendo's own agent addresses it when it sets `aria-expanded` — the
 * underscore prefix is theirs. Their unread bubble lives in this same element, so it is
 * also where a dot belongs.
 */
const LAUNCHER = 'button._pendo-resource-center-badge-container, ._pendo-resource-center-badge-container';
/** What Pendo hangs on each module row of the home view, holding that module's guide id. */
const MODULE_ATTR = 'data-pendo-module-guide-id';
const LAUNCHER_DOT_ID = 'up-product-news-dot';
const ROW_DOT_ID = 'up-product-news-row-dot';
/**
 * Produktnyheter's module, as Pendo identifies it. Read off the live Resource Center
 * because it is account data rather than anything this repo owns, and the same in every
 * environment: the Pendo api key is hardcoded in `ui/index.html`, so one subscription —
 * and one set of module ids — serves them all.
 *
 * Named rather than positional or matched on its label: the rows can be reordered in
 * Pendo's dashboard, and the label is translated.
 */
const MODULE_GUIDE_ID = 'AbmilcyKNOQ8Ie0fdV4tRVj_lLI@qDqjxU4kk6wkgMWb5LmbirjW9wk';
/**
 * Where the module id is remembered when the one above stops matching — rebuild the
 * module in Pendo and it takes a new guide id, which would otherwise leave the row
 * unbadged for good. Opening the module identifies it: the announcements view the
 * releases are inserted into IS that module, so the row clicked immediately beforehand is
 * the one.
 */
const MODULE_KEY = 'upsales:productNews:moduleGuideId';

let unreadCount = 0;
let lastModuleOpened: string | null = null;

/**
 * The bubble, carrying how many releases are unread. Drawn rather than borrowed: Pendo's
 * own bubble holds a count it derives from its own unseen guides, so anything written
 * into it is overwritten the next time they recount.
 *
 * Shaped like theirs — a pill that stays circular at one digit and grows for two — and
 * labelled rather than hidden from assistive tech, because the number is the message.
 */
const bubble = (id: string, count: number, css: string) => {
	const el = document.createElement('span');
	el.id = id;
	el.textContent = count > 9 ? '9+' : String(count);
	el.setAttribute('role', 'status');
	el.setAttribute('aria-label', T('productNews.unread', { count: String(count) }));
	el.style.cssText =
		'min-width:18px;height:18px;padding:0 5px;border-radius:9px;background:#c62d2d;color:#fff;' +
		'font-size:11px;line-height:18px;font-weight:700;text-align:center;box-sizing:border-box;' +
		`font-family:inherit;${css}`;
	return el;
};

const readModuleGuideId = () => {
	try {
		return window.localStorage.getItem(MODULE_KEY);
	} catch {
		// Private mode, or storage full. The launcher dot does not depend on this.
		return null;
	}
};

const rememberModuleGuideId = (id: string) => {
	try {
		window.localStorage.setItem(MODULE_KEY, id);
	} catch {
		// Nothing to do about it; the row dot simply stays absent — and a dot that cannot
		// be placed is not worth failing the panel over.
	}
};

/**
 * Put one badge where it belongs, or take it away. Rebuilt rather than mutated when the
 * count moves — a bubble showing a stale number is worse than none.
 */
const place = (id: string, host: Element | null | undefined, css: string) => {
	const existing = document.getElementById(id);
	if (!host || !unreadCount) {
		existing?.remove();
		return;
	}
	if (existing) {
		if (
			existing.parentElement === host &&
			existing.textContent === (unreadCount > 9 ? '9+' : String(unreadCount))
		) {
			return;
		}
		existing.remove();
	}
	host.appendChild(bubble(id, unreadCount, css));
};

/**
 * The Produktnyheter row.
 *
 * Searched across the whole panel rather than under a named list: Pendo's own code
 * addresses these rows with a bare `[data-pendo-module-guide-id]` query and never scopes
 * it either, and the list around them is not reliably the id their bundle suggests.
 *
 * Matched by reading the attribute rather than by building a selector out of a stored
 * value — nothing here has to be escaped, and it needs no `CSS.escape`.
 */
const findModuleRow = () => {
	const container = document.getElementById(PENDO_CONTAINER_ID) ?? document;
	const rows = Array.from(container.querySelectorAll(`[${MODULE_ATTR}]`));
	// The constant first, the learned id second: a rebuilt module leaves the constant
	// matching nothing, and then whatever the reader last opened is the better answer.
	for (const wanted of [MODULE_GUIDE_ID, readModuleGuideId()]) {
		const match = wanted && rows.find(el => el.getAttribute(MODULE_ATTR) === wanted);
		if (match) {
			return match;
		}
	}
	return null;
};

/** Both badges follow the unread count, so one function puts them right whatever changed. */
export const renderProductNewsBadges = () => {
	// Their launcher is the positioning context for their own bubble, so it is for this
	// too; inset rather than overhanging, which theirs does.
	place(
		LAUNCHER_DOT_ID,
		document.querySelector(LAUNCHER),
		'position:absolute;top:-2px;right:-2px;box-shadow:0 0 0 2px #fff'
	);
	// Left of their caret, vertically centred on the row.
	place(ROW_DOT_ID, findModuleRow(), 'position:absolute;top:50%;right:34px;margin-top:-9px');
};

export const setProductNewsUnread = (count: number) => {
	unreadCount = count;
	renderProductNewsBadges();
};

/**
 * The announcements view is open, so the reader is looking at the news. Also names the
 * module: that view IS Produktnyheter, so the row clicked to reach it is the one to
 * remember. Answers whether there was anything unread to clear.
 */
export const productNewsPanelOpened = () => {
	if (lastModuleOpened) {
		rememberModuleGuideId(lastModuleOpened);
	}
	if (!unreadCount) {
		return false;
	}
	setProductNewsUnread(0);
	return true;
};

/**
 * Which module row the user last clicked, so that opening the announcements view can name
 * it. Capture phase, because Pendo replaces the home view with the sub-view on the same
 * click.
 */
const onModuleClick = (event: Event) => {
	const target = event.target as Element | null;
	const module = target?.closest?.(`[${MODULE_ATTR}]`);
	lastModuleOpened = module?.getAttribute(MODULE_ATTR) ?? lastModuleOpened;
};

export const watchProductNewsModuleClicks = () => {
	document.addEventListener('click', onModuleClick, true);
};

/** The counterpart: drops the listener and both dots. Tests need it; the app does not. */
export const uninstallProductNewsBadges = () => {
	document.removeEventListener('click', onModuleClick, true);
	document.getElementById(LAUNCHER_DOT_ID)?.remove();
	document.getElementById(ROW_DOT_ID)?.remove();
	unreadCount = 0;
	lastModuleOpened = null;
};
